// El editor de un post — ADR-2026-10-06-el-post-se-edita-solo y
// ADR-2026-10-07-el-post-se-guarda-con-un-boton.
//
// Ocupa la pantalla entera: el formulario y, al lado, cómo se va a ver en el feed. Lo que
// se escribe es un BORRADOR hasta tocar "Guardar post": texto, fecha, piezas que se sacan y
// el orden viven en la pantalla. Las piezas que se suman sí se suben al momento —necesitan
// la fila del post para colgarse—, y si se descarta se sacan. Al irse con algo sin guardar
// se pregunta: guardar, descartar o seguir editando.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlignLeft,
  CalendarClock,
  Check,
  ChevronLeft,
  ClipboardCheck,
  ClipboardCopy,
  Download,
  Images,
  Loader2,
  ChevronRight,
  Eye,
  Link2,
  Lock,
  MessageCircle,
  MessageSquare,
  MoreHorizontal,
  Play,
  Sparkles,
  Target,
  Trash2,
  Wand2,
  X,
} from 'lucide-react';
import SortableImageList from '../../../components/tnexport/SortableImageList';
import { RUTA_CHAT_ATLAS, useChatAtlasCascaron } from '../../../chat-atlas';
import { registrarConfirmacionDeCambioDeMarca, useBrandProfile } from '../../../hooks/useBrandProfile';
import { useSidebar } from '../../../context/SidebarContext';
import { HojaMovil } from './HojaMovil';
import { useUserGroup } from '../../../hooks/useUserGroup';
import { seVenLasMetricas } from './avisos';
import { ComoLeFue } from './metricas/ComoLeFue';
import { descargarSueltos } from '../../../utils/descargarLote';
import { boton } from '../botones';
import VisorDeFoto from '../VisorDeFoto';
import { AgregarFoto } from '../AgregarFoto';
import { Seccion } from '../Seccion';
import { SelectorDeCreaciones } from '../SelectorDeCreaciones';
import { useRecorrido } from '../recorridos';
import {
  firmarFull,
  quitarImagen,
  reordenarImagenes,
  subirImagenAProducto,
  sumarGeneradasAProducto,
} from '../lanzamientosApi';
import { escribirCopy } from './copyIa';
import { nombresDeLasPiezas } from './descargarPost';
import { comoSeLee, sugerirMomento, type PostEnAgenda } from './mejorMomento';
import {
  MAX_PIEZAS,
  MAX_TEXTO,
  avisoDeSeccion,
  contarHashtags,
  formatoDe,
  nombreDelFormato,
  problemasConSeccion,
  type MedioDePost,
} from './post';
import { borrarPost, guardarPost, type PostConMiniaturas } from './postsApi';
import PanelDeComentarios from '../Comentarios';
import { useComentarios } from '../useComentarios';
import { pasosDelPost } from './recorridoDelCalendario';
import { CajaDeReferencias } from './CajaDeReferencias';
import { CrearConElAgente } from './CrearConElAgente';
import { useGuardiaDeSalida, type DestinoFrenado } from './useGuardiaDeSalida';
import { VistaPreviaDeInstagram } from './VistaPreviaDeInstagram';

/** `<input type="date">` quiere `2026-09-15`; `type="time"`, `18:00`. En hora LOCAL. */
function partesDeFecha(d: Date | null): { dia: string; hora: string } {
  if (!d) return { dia: '', hora: '' };
  const p = (n: number) => String(n).padStart(2, '0');
  return {
    dia: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`,
    hora: `${p(d.getHours())}:${p(d.getMinutes())}`,
  };
}

/** Los dos inputs de vuelta a una fecha. `null` si falta alguno. */
function aFecha(dia: string, hora: string): Date | null {
  if (!dia || !hora) return null;
  const [a, m, d] = dia.split('-').map(Number);
  const [hh, mm] = hora.split(':').map(Number);
  if (!a || !m || !d) return null;
  // Por componentes y NO `new Date("2026-09-15T18:00")`: esa forma la interpretan
  // distinto los navegadores y el post se movía de hora solo.
  return new Date(a, m - 1, d, hh ?? 0, mm ?? 0);
}

/** "miércoles 14 de octubre a las 12:00": la fecha entera, para el encabezado. */
export function fechaLarga(d: Date): string {
  // Sin la coma que pone `toLocaleDateString` ("lunes, 12 de octubre"): se lee de corrido.
  const dia = d
    .toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })
    .replace(',', '');
  return `${dia} a las ${partesDeFecha(d).hora}`;
}

/** Cómo se sale del editor: con su propio volver, o frenado por la guardia. */
type Salida =
  | { tipo: 'propia' }
  | DestinoFrenado
  // Otra cosa de la pantalla quiere cambiar de post (el agente con `abrir_post`): primero
  // se resuelve el borrador de éste, y `seguir` hace el cambio (review Greptile #169).
  | { tipo: 'accion'; seguir: () => void; cancelar?: () => void };

/**
 * La card "Planificación y referencias" (y su "Crear con IA") está OCULTA: casi no se usaba
 * y confundía (ADR-2026-10-13-se-oculta-la-planificacion-del-post). Los datos siguen en la
 * base y se siguen guardando si existen; volver a mostrarla es cambiar esta constante.
 */
const MOSTRAR_PLANIFICACION = false;

export function EditorDePost({
  post,
  companyId,
  agenda,
  desde,
  aceptaVideo,
  delAgente = false,
  esNuevo = false,
  onCambio,
  onGuardado,
  onCerrar,
  registrarSalida,
}: {
  post: PostConMiniaturas;
  companyId: string;
  /** Todo lo planificado de la empresa, para no pisarse. */
  agenda: PostEnAgenda[];
  /** No se agenda antes de acá. */
  desde: Date | null;
  aceptaVideo: boolean;
  /** Lo armó el agente en esta sesión: se dice arriba, para revisarlo con ese ojo. */
  delAgente?: boolean;
  /** Recién creado con "Nuevo post" y nunca guardado: descartarlo lo borra. */
  esNuevo?: boolean;
  /** Releer el calendario (se subió o se sacó algo de la base). */
  onCambio: () => void;
  /** Se guardó: el calendario lo cuenta y deja de tratarlo como nuevo. */
  onGuardado: (cuando: Date) => void;
  /** Volver al calendario. */
  onCerrar: () => void;
  /**
   * El calendario pide salir del post por otro camino (el agente abre otro post). El editor
   * le da una función que pasa por la MISMA pregunta de salida y después llama a `seguir`.
   */
  registrarSalida?: (salir: ((seguir: () => void) => void) | null) => void;
}) {
  const { activeBrand, profile, currentBrandId, loading: cargandoMarca } = useBrandProfile();
  const { isDevTester } = useUserGroup();
  /** Instagram dice que el post publicado ya no existe ("Cómo le fue" lo averigua). */
  const [borradoEnInstagram, setBorradoEnInstagram] = useState(false);
  const { abrirChat } = useChatAtlasCascaron();

  // ── Comentarios ──────────────────────────────────────────────────────────
  // El post ES una fila de `lanzamientos_productos`, así que reutiliza el mismo
  // sistema de comentarios que la ficha de un producto.
  const [verComentarios, setVerComentarios] = useState(false);
  const com = useComentarios(post.id, companyId);

  /**
   * Formulario y vista previa lado a lado según el ancho del EDITOR, no de la ventana: con
   * el chat abierto al costado la ventana es ancha pero al editor le quedan ~850px, y una
   * vista previa fija dejaba el formulario en una tira de fotos de a una.
   */
  const cuerpoRef = useRef<HTMLDivElement>(null);
  const [ancho, setAncho] = useState(0);
  useEffect(() => {
    const el = cuerpoRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => setAncho(e?.contentRect.width ?? 0));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const lado = ancho >= 760;

  // ── El borrador ─────────────────────────────────────────────────────────
  /** Lo último guardado: contra esto se mide si hay cambios. */
  const [base, setBase] = useState(() => ({
    texto: post.texto,
    planificacion: post.planificacion,
    referencias: post.referencias,
    ...partesDeFecha(post.cuando),
  }));
  /** Las piezas que el post tenía al abrirlo (o al último guardado). Las demás son nuevas. */
  const [idsBase, setIdsBase] = useState(() => new Set(post.medios.map((m) => m.id)));
  const [texto, setTexto] = useState(post.texto);
  /** Notas de trabajo, no se publican (ADR-2026-10-08-el-post-tiene-planificacion-y-referencias). */
  const [planificacion, setPlanificacion] = useState(post.planificacion);
  /**
   * Una descripción que escribió el agente mientras el usuario tenía la suya distinta. No se
   * le pisa lo que escribió, pero tampoco se ignora en silencio: se le ofrece con "Usarla".
   * Antes quedaba descartada y, si después guardaba, pisaba la del agente sin saberlo.
   */
  const [propuestaTexto, setPropuestaTexto] = useState<string | null>(null);
  const [referencias, setReferencias] = useState(post.referencias);
  const [{ dia, hora }, setCuando] = useState(() => partesDeFecha(post.cuando));
  /** Piezas sacadas en pantalla: se borran al guardar. */
  const [quitadas, setQuitadas] = useState<Set<string>>(() => new Set());
  /** El orden arrastrado en pantalla, o null si es el de la base. */
  const [orden, setOrden] = useState<string[] | null>(null);
  /** Se intentó guardar: recién ahí un post nuevo muestra lo que le falta. */
  const [intentoGuardar, setIntentoGuardar] = useState(false);
  const [guardando, setGuardando] = useState(false);
  /** La pregunta de salida abierta, y adónde iba. */
  const [salida, setSalida] = useState<Salida | null>(null);

  const [porque, setPorque] = useState<string | null>(null);
  const [escribiendo, setEscribiendo] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [eligiendo, setEligiendo] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  /** Qué pieza se está mirando ampliada, y su URL completa ya firmada. */
  const [viendo, setViendo] = useState<string | null>(null);
  const [urlAmpliada, setUrlAmpliada] = useState<string | null>(null);
  const [bajando, setBajando] = useState(false);
  const [copiado, setCopiado] = useState(false);

  /**
   * Si el agente cambia el post mientras está abierto (`modificar_post`), lo nuevo entra
   * al borrador SÓLO en lo que el usuario no tocó: pisarle lo que está escribiendo sería
   * perderle trabajo.
   */
  const vivo = useRef({ texto, dia, hora, base, planificacion, referencias });
  vivo.current = { texto, dia, hora, base, planificacion, referencias };
  useEffect(() => {
    const v = vivo.current;
    if (post.texto === v.base.texto) return;
    if (v.texto === v.base.texto) setTexto(post.texto);
    else if (post.texto !== v.texto) setPropuestaTexto(post.texto);
    setBase((b) => ({ ...b, texto: post.texto }));
  }, [post.texto]);
  useEffect(() => {
    const v = vivo.current;
    if (post.planificacion !== v.base.planificacion) {
      if (v.planificacion === v.base.planificacion) setPlanificacion(post.planificacion);
      setBase((b) => ({ ...b, planificacion: post.planificacion }));
    }
    if (post.referencias !== v.base.referencias) {
      if (v.referencias === v.base.referencias) setReferencias(post.referencias);
      setBase((b) => ({ ...b, referencias: post.referencias }));
    }
  }, [post.planificacion, post.referencias]);
  const cuandoMs = post.cuando?.getTime() ?? null;
  useEffect(() => {
    const v = vivo.current;
    const nuevo = partesDeFecha(post.cuando);
    if (nuevo.dia === v.base.dia && nuevo.hora === v.base.hora) return;
    if (v.dia === v.base.dia && v.hora === v.base.hora) setCuando(nuevo);
    setBase((b) => ({ ...b, ...nuevo }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuandoMs]);

  /** Las piezas como se ven en pantalla: sin las sacadas y en el orden arrastrado. */
  const { medios, miniaturas, agregadas, ordenCambio } = useMemo(() => {
    const porId = new Map(post.medios.map((m, i) => [m.id, { m, thumb: post.miniaturas[i] ?? '' }]));
    const deLaBase = post.medios.map((m) => m.id).filter((id) => !quitadas.has(id));
    const arrastrados = (orden ?? []).filter((id) => porId.has(id) && !quitadas.has(id));
    const ids = [...arrastrados, ...deLaBase.filter((id) => !arrastrados.includes(id))];
    return {
      medios: ids.map((id, i): MedioDePost => ({ ...porId.get(id)!.m, orden: i })),
      miniaturas: ids.map((id) => porId.get(id)!.thumb),
      agregadas: post.medios.map((m) => m.id).filter((id) => !idsBase.has(id)),
      ordenCambio: ids.join() !== deLaBase.join(),
    };
  }, [post.medios, post.miniaturas, quitadas, orden, idsBase]);

  const fecha = aFecha(dia, hora);
  const formato = formatoDe(medios);
  const problemas = problemasConSeccion({ ...post, medios, texto, cuando: fecha });
  const tags = contarHashtags(texto);
  /** Lo que falta se muestra cuando ya importa: en un post nuevo, después de querer guardar. */
  const mostrarFaltantes = !esNuevo || intentoGuardar;
  const avisoDe = (s: 'medios' | 'texto' | 'cuando') =>
    mostrarFaltantes ? avisoDeSeccion(problemas, s) : null;

  const sinGuardar = esNuevo
    ? medios.length > 0 || texto.trim() !== '' || planificacion.trim() !== '' || referencias.trim() !== ''
    : texto !== base.texto ||
      planificacion !== base.planificacion ||
      referencias !== base.referencias ||
      dia !== base.dia ||
      hora !== base.hora ||
      quitadas.size > 0 ||
      agregadas.length > 0 ||
      ordenCambio;

  // ── Salir ───────────────────────────────────────────────────────────────
  // Un post nuevo y vacío no pregunta nada, pero igual se intercepta la salida para
  // borrarlo: si no, quedaba un fantasma en el mes
  // (ADR-2026-09-15-un-post-al-que-no-se-le-puso-nada-se-descarta).
  const guardia = useGuardiaDeSalida({ preguntar: sinGuardar, interceptar: sinGuardar || esNuevo }, (d) =>
    intentarSalir(d),
  );

  const terminar = (s: Salida) => {
    setSalida(null);
    if (s.tipo === 'propia') {
      guardia.retirar();
      onCerrar();
    } else if (s.tipo === 'accion') {
      guardia.retirar();
      s.seguir();
    } else {
      guardia.continuar(s);
    }
  };

  /** Todas las salidas pasan por acá: si hay algo sin guardar, se pregunta. */
  function intentarSalir(s: Salida) {
    if (sinGuardar) return setSalida(s);
    // Mientras una subida está en vuelo el post todavía no tiene la pieza: no está vacío
    // (ADR-2026-09-16-un-post-con-algo-en-vuelo-no-esta-vacio).
    if (esNuevo && !subiendo && !escribiendo) void borrarPost(post.id).then(onCambio);
    terminar(s);
  }

  /** Guarda el borrador. `false` si no se pudo (y dice por qué). */
  const guardar = async (): Promise<boolean> => {
    // Ya salió o está saliendo: lo que se guarde ahora no llega a Instagram. El candado va acá y no sólo en
    // los botones: la pregunta de salida ("Guardar cambios") también pasa por acá (review Greptile #225).
    if (enInstagram) {
      setAviso(
        publicado
          ? 'Este post ya se publicó en Instagram: los cambios no le llegan.'
          : 'Este post se está publicando en Instagram. Esperá a que termine.',
      );
      return false;
    }
    setIntentoGuardar(true);
    if (!fecha) {
      setAviso('Elegí el día y la hora en que sale el post.');
      return false;
    }
    setGuardando(true);
    setAviso(null);
    const errores: string[] = [];
    const g = await guardarPost(post.id, { texto, cuando: fecha, planificacion, referencias });
    if (g.error) errores.push(g.error);
    for (const id of quitadas) {
      const q = await quitarImagen(id);
      if (q.error) errores.push(q.error);
    }
    if (ordenCambio && medios.length > 0) {
      const r = await reordenarImagenes(medios.map((m) => m.id));
      if (r.error) errores.push(r.error);
    }
    setGuardando(false);
    onCambio();
    if (errores.length > 0) {
      setAviso(errores[0]!);
      return false;
    }
    setBase({ texto, dia, hora, planificacion, referencias });
    setIdsBase(new Set(medios.map((m) => m.id)));
    setQuitadas(new Set());
    setOrden(null);
    onGuardado(fecha);
    return true;
  };

  /** Deja todo como estaba: un post nuevo se borra; uno existente pierde lo que se le sumó. */
  const descartar = async () => {
    if (esNuevo) await borrarPost(post.id);
    else for (const id of agregadas) await quitarImagen(id);
    onCambio();
  };

  const guardarYVolver = async (s: Salida = { tipo: 'propia' }) => {
    if (await guardar()) return terminar(s);
    // No se pudo guardar (sin fecha, error de red): se queda. Si lo frenó el atrás del
    // navegador, esa entrada de guardia ya se gastó y se vuelve a poner — si no, el próximo
    // atrás salía sin preguntar y perdía el borrador (review Greptile #169).
    if (s.tipo === 'atras') guardia.quedarse(s);
    setSalida(null);
  };

  // ── Lo demás ────────────────────────────────────────────────────────────

  // La URL completa se firma recién al abrir el visor: firmar las diez de
  // entrada sería diez llamadas para una que se va a mirar.
  useEffect(() => {
    if (!viendo) return setUrlAmpliada(null);
    const m = post.medios.find((x) => x.id === viendo);
    if (!m) return;
    let sigue = true;
    void firmarFull(companyId, m.fileName).then((u) => {
      if (sigue) setUrlAmpliada(u);
    });
    return () => {
      sigue = false;
    };
  }, [viendo, companyId, post.medios]);

  /**
   * El horario sugerido, A LA VISTA y no detrás de un botón (Later en Mobbin): saber que
   * hay uno mejor es la mitad del valor; aplicarlo es un click.
   */
  const sugerencia = useMemo(
    () => sugerirMomento({ agenda, ahora: new Date(), desde, excluirId: post.id }),
    [agenda, desde, post.id],
  );
  const esLaSugerida = !!fecha && fecha.getTime() === sugerencia.cuando.getTime();

  const sugerir = () => {
    setCuando(partesDeFecha(sugerencia.cuando));
    setPorque(sugerencia.porque);
  };

  /** El chat con el pedido empezado: el usuario termina la frase y lo manda. */
  const pedirleAlAgente = () =>
    abrirChat({
      contexto: { pantalla: 'el calendario de Instagram', ruta: '/instagram', detalle: `post_id=${post.id}` },
      vista: 'panel',
      mensajeInicial: `Sobre el post del ${fecha ? comoSeLee(fecha) : 'calendario'}: `,
    });

  const borrar = () =>
    void borrarPost(post.id).then(() => {
      onCambio();
      guardia.retirar();
      onCerrar();
    });

  const conIa = async () => {
    setEscribiendo(true);
    setAviso(null);
    const r = await escribirCopy(medios.map((m) => m.fileName).filter(Boolean));
    setEscribiendo(false);
    if (r.error || !r.texto) return setAviso(r.error);
    setTexto(r.texto);
  };

  const subir = async (f: File) => {
    if (enInstagram) return;
    setSubiendo(true);
    setAviso(null);
    const r = await subirImagenAProducto(post.id, companyId, f, null, aceptaVideo);
    setSubiendo(false);
    if (r.error) setAviso(r.error);
    else onCambio();
  };

  /**
   * Bajar las piezas del post, SUELTAS y nunca en un ZIP (pedido de Mati): de un post se
   * sacan las piezas para subirlas a otro lado. `descargarSueltos` las manda de a una,
   * corrige la extensión por los bytes y saltea la que falle sin cortar el resto.
   */
  const descargar = async () => {
    if (medios.length === 0 || bajando) return;
    setBajando(true);
    setAviso(null);
    const nombres = nombresDeLasPiezas(medios, fecha);
    const urls = await Promise.all(medios.map((m) => firmarFull(companyId, m.fileName)));
    const archivos = medios
      // `imagenId`: las piezas que son creaciones quedan marcadas como descargadas.
      .map((m, i) => ({
        url: urls[i] ?? '',
        nombre: nombres[i] ?? 'post',
        imagenId: m.generadaId ?? undefined,
      }))
      .filter((a) => a.url);
    // Las que no se pudieron firmar no llegan a `descargarSueltos`: se suman al conteo
    // para no decir "2 ok, 0 fallaron" de un carrusel incompleto.
    const sinFirmar = medios.length - archivos.length;
    const r = await descargarSueltos(archivos);
    const fallidos = r.fallidos + sinFirmar;
    setBajando(false);
    if (r.ok === 0) setAviso('No se pudo descargar');
    else if (fallidos > 0) setAviso(`Se bajaron ${r.ok}; ${fallidos} fallaron`);
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 1800);
    } catch {
      // Pasa sin HTTPS y con el portapapeles bloqueado por el navegador.
      setAviso('El navegador no dejó copiar. Seleccioná el texto y copialo a mano.');
    }
  };

  // La primera vez que se abre un post, los cartelitos del editor.
  useRecorrido('instagram_tour_post', true, pasosDelPost);

  const publicado = post.estado === 'publicado';
  /** Ya salió o está saliendo: guardar cambios ahora no los publica (ADR-2026-10-28-instagram-publica-el-calendario). */
  const enInstagram = publicado || post.estado === 'publicando';

  // ── En el teléfono ──────────────────────────────────────────────────────
  // (ADR-2026-10-07-el-calendario-en-el-telefono) Un solo volver: el flotante de la app,
  // que pasa por la misma pregunta de salida. Sin la barra de abajo de la app: su lugar
  // lo toma la barra de "Guardar post", a mano del pulgar (el "Share" de Instagram).
  const [hoja, setHoja] = useState<null | 'acciones' | 'cuando' | 'vista' | 'comentarios'>(null);
  const { setMobileBackOverride, setMobileHideBottomNav } = useSidebar();
  const salirRef = useRef(intentarSalir);
  salirRef.current = intentarSalir;
  useEffect(() => {
    registrarSalida?.((seguir) => salirRef.current({ tipo: 'accion', seguir }));
    return () => registrarSalida?.(null);
  }, [registrarSalida]);

  // Cambiar de marca desmonta el calendario: con algo que perder, pasa por la misma pregunta
  // (ADR-2026-10-08-cambiar-de-marca-pregunta-si-hay-cambios). "Seguir editando" lo cancela.
  const hayQuePreguntar = sinGuardar || esNuevo;
  useEffect(() => {
    if (!hayQuePreguntar) return;
    registrarConfirmacionDeCambioDeMarca(
      () =>
        new Promise<boolean>((resolver) =>
          salirRef.current({ tipo: 'accion', seguir: () => resolver(true), cancelar: () => resolver(false) }),
        ),
    );
    return () => registrarConfirmacionDeCambioDeMarca(null);
  }, [hayQuePreguntar]);
  useEffect(() => {
    setMobileBackOverride(() => () => salirRef.current({ tipo: 'propia' }));
    setMobileHideBottomNav(true);
    return () => {
      setMobileBackOverride(null);
      setMobileHideBottomNav(false);
    };
  }, [setMobileBackOverride, setMobileHideBottomNav]);

  /** La fecha y la hora, con el horario sugerido: en la sección (escritorio) o en su hoja. */
  const camposDelCuando = (
    <>
      <div className="flex gap-2">
        <input
          type="date"
          value={dia}
          onChange={(e) => {
            const v = e.target.value;
            setCuando((p) => ({ ...p, dia: v }));
            setPorque(null);
          }}
          className="min-w-0 flex-1 rounded-xl border border-neutral-200 py-2.5 px-3 text-sm text-brand-black outline-none focus:border-brand-primary focus:ring-4 focus:ring-[rgb(var(--brand-primary-rgb)_/_0.07)]"
        />
        <input
          type="time"
          value={hora}
          onChange={(e) => {
            const v = e.target.value;
            setCuando((p) => ({ ...p, hora: v }));
            setPorque(null);
          }}
          className="w-36 shrink-0 rounded-xl border border-neutral-200 py-2.5 px-3 text-sm text-brand-black outline-none focus:border-brand-primary focus:ring-4 focus:ring-[rgb(var(--brand-primary-rgb)_/_0.07)]"
        />
      </div>
      {!publicado &&
        (esLaSugerida ? (
          <p className="flex items-center gap-1.5 rounded-xl bg-emerald-50 py-2.5 px-3 text-xs text-emerald-800">
            <Check className="w-3.5 h-3.5 shrink-0" />
            <span className="min-w-0">Es el horario sugerido{porque ? `: ${porque}` : '.'}</span>
          </p>
        ) : (
          <div className="flex items-center gap-3 rounded-xl bg-[rgb(var(--brand-primary-rgb)_/_0.07)] py-2.5 px-3">
            <Wand2 className="w-4 h-4 shrink-0 text-brand-primary" />
            <span className="min-w-0 flex-1 text-xs text-brand-black">
              <span className="font-semibold first-letter:uppercase">
                Sugerido: {comoSeLee(sugerencia.cuando)}
              </span>
              <span className="block truncate text-neutral-500" title={sugerencia.porque}>
                {sugerencia.porque}
              </span>
            </span>
            <button type="button" onClick={sugerir} className={boton('primario', { chico: true })}>
              Usar
            </button>
          </div>
        ))}
    </>
  );

  /**
   * "Crear con IA", uno solo, abajo a la derecha de la card de planificación y referencias:
   * guarda el post y lleva a la sección del Agente con las dos notas escritas en la caja
   * (ADR-2026-10-08-crear-con-ia-va-a-la-seccion-del-agente).
   */
  const botonCrear = publicado ? null : (
    <CrearConElAgente
      cuando={fecha ? fechaLarga(fecha) : null}
      planificacion={planificacion}
      referencias={referencias}
      guardarAntes={async () => !sinGuardar || (await guardar())}
      // Por la guardia: si dejó una entrada de historial, la reemplaza en vez de apilar otra.
      irAlAgente={() => guardia.continuar({ tipo: 'ruta', a: RUTA_CHAT_ATLAS })}
    />
  );

  const vistaPrevia = (
    <VistaPreviaDeInstagram
      medios={medios}
      miniaturas={miniaturas}
      texto={texto}
      cuando={fecha}
      marca={activeBrand?.name || profile?.company_name || ''}
      logo={activeBrand?.logo_url ?? null}
    />
  );

  return (
    <div className="pb-28 md:pb-0">
      {/* ⚠ El `<h1>` es hijo DIRECTO de esta fila (ADR-2026-09-15-el-titulo-no-se-puede-envolver):
          el volver y las acciones son sus hermanos. Con un post abierto, el encabezado de la
          sección ("Instagram · N posts") no se dibuja: hablaría de otra cosa. */}
      <div className="flex flex-wrap items-center gap-y-3">
        <button
          type="button"
          onClick={() => intentarSalir({ tipo: 'propia' })}
          aria-label="Volver al calendario"
          title="Volver al calendario"
          className="-ml-1 mr-1 hidden h-7 w-7 shrink-0 place-items-center rounded-lg text-brand-black hover:bg-neutral-100 md:grid"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <h1 className="min-w-0 flex-1 truncate text-xl font-semibold text-brand-black md:text-2xl">
          {esNuevo ? 'Nuevo post' : 'Editar post'}
        </h1>
        {/* Teléfono: el agente y el resto de las acciones, como íconos. */}
        <div className="ml-3 flex shrink-0 items-center gap-2 md:hidden">
          <button
            type="button"
            onClick={pedirleAlAgente}
            aria-label="Pedirle al asistente"
            className="grid h-10 w-10 place-items-center rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.07)] text-brand-primary"
          >
            <Sparkles className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setHoja('acciones')}
            aria-label="Más acciones"
            className="grid h-10 w-10 place-items-center rounded-full bg-neutral-100 text-brand-black"
          >
            <MoreHorizontal className="w-5 h-5" />
          </button>
        </div>
        <div className="ml-4 hidden shrink-0 items-center gap-3 md:flex">
          {sinGuardar && !esNuevo && (
            <span className="hidden items-center gap-1.5 text-xs font-medium text-neutral-500 sm:flex">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> Cambios sin guardar
            </span>
          )}
          <button
            type="button"
            onClick={() => void guardarYVolver()}
            disabled={guardando || enInstagram}
            data-tour="ig-guardar"
            className={boton('primario')}
          >
            {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {guardando ? 'Guardando…' : 'Guardar post'}
          </button>
        </div>
      </div>
      <p className="mt-1 text-sm text-neutral-500 md:pl-7">
        Instagram · {nombreDelFormato(formato)}
        {fecha ? ` · sale el ${fechaLarga(fecha)}` : ' · todavía sin fecha'}
        {delAgente && (
          <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.07)] py-0.5 px-2 align-middle text-xs font-medium text-brand-primary">
            <Sparkles className="w-3 h-3" /> Lo armó el asistente
          </span>
        )}
      </p>

      {/* Las acciones del post. El estado entra recién cuando importa (un post nuevo no
          arranca diciendo todo lo que le falta). */}
      <div className="mt-5 hidden flex-wrap items-center gap-2 md:flex">
        <button
          type="button"
          onClick={pedirleAlAgente}
          className={boton('secundario', { chico: true, extra: 'py-2' })}
        >
          <MessageCircle className="w-3.5 h-3.5" /> Pedirle al asistente
        </button>
        <button
          type="button"
          onClick={() => void descargar()}
          disabled={medios.length === 0 || bajando}
          className={boton('secundario', { chico: true, extra: 'py-2' })}
        >
          {bajando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          {bajando ? 'Bajando…' : 'Descargar'}
        </button>
        <button
          type="button"
          onClick={() => void copiar()}
          disabled={!texto.trim()}
          className={boton('secundario', { chico: true, extra: 'py-2' })}
        >
          {copiado ? <ClipboardCheck className="w-3.5 h-3.5" /> : <ClipboardCopy className="w-3.5 h-3.5" />}
          {copiado ? 'Copiada' : 'Copiar descripción'}
        </button>
        <button
          type="button"
          onClick={() => setVerComentarios(true)}
          className={boton('secundario', { chico: true, extra: 'py-2' })}
        >
          <MessageSquare className="w-3.5 h-3.5" /> Comentarios
          {com.comentarios.filter((c) => !c.resuelto).length > 0 && (
            <span className="ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-400 px-1 text-[11px] font-semibold text-white">
              {com.comentarios.filter((c) => !c.resuelto).length}
            </span>
          )}
        </button>
        <span className="ml-auto flex items-center gap-2">
          {publicado ? (
            <span className="rounded-full bg-neutral-100 py-1 px-2.5 text-xs font-medium text-neutral-600">
              Publicado
            </span>
          ) : (
            mostrarFaltantes && (
              <span
                className={`flex items-center gap-1.5 rounded-full py-1 px-2.5 text-xs font-medium ${
                  problemas.length === 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800'
                }`}
              >
                {problemas.length === 0 ? (
                  <>
                    <Check className="w-3.5 h-3.5" /> Listo
                  </>
                ) : (
                  `Falta ${problemas.length > 1 ? `${problemas.length} datos` : '1 dato'}`
                )}
              </span>
            )
          )}
          <button
            type="button"
            onClick={esNuevo ? () => void descartar().then(() => terminar({ tipo: 'propia' })) : borrar}
            className={boton('peligro', { chico: true, extra: 'py-2' })}
          >
            <Trash2 className="w-3.5 h-3.5" /> {esNuevo ? 'Descartar' : 'Borrar'}
          </button>
        </span>
      </div>

      {mostrarFaltantes && !publicado && problemas.length > 0 && (
        <ul className="mt-3 space-y-1 rounded-xl bg-amber-50 py-2.5 px-3 text-xs text-amber-900">
          {problemas.map((x) => (
            <li key={x.texto} className="flex items-center gap-1.5">
              <span className="h-1 w-1 shrink-0 rounded-full bg-amber-500" /> {x.texto}
            </li>
          ))}
        </ul>
      )}

      {/* Lo que pasó al publicar en Instagram (ADR-2026-10-28-instagram-publica-el-calendario):
          el motivo si no salió, que se está publicando, o el link si ya salió. */}
      {post.estado === 'error' && post.error && (
        <p className="mt-3 rounded-xl bg-red-50 py-2.5 px-3 text-xs text-red-800">
          <span className="font-semibold">No se publicó.</span> {post.error}
        </p>
      )}
      {post.estado === 'publicando' && (
        <p className="mt-3 flex items-center gap-1.5 rounded-xl bg-neutral-100 py-2.5 px-3 text-xs text-neutral-600">
          <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin" /> Publicando en Instagram…
        </p>
      )}
      {publicado && post.permalink && !borradoEnInstagram && (
        <a
          href={post.permalink}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-brand-primary hover:underline"
        >
          <Link2 className="w-3.5 h-3.5" /> Ver en Instagram
        </a>
      )}
      {/* Los números del post publicado, o que ya no está en Instagram
          (ADR-2026-10-29-dashboard-de-metricas-de-instagram). */}
      {publicado && post.igMediaId && seVenLasMetricas(isDevTester) && !cargandoMarca && (
        <ComoLeFue brandId={currentBrandId} mediaId={post.igMediaId} onBorrado={setBorradoEnInstagram} />
      )}

      {aviso && (
        <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 py-3 px-4 text-sm text-amber-900">
          {aviso}
        </p>
      )}

      {/* El formulario a la izquierda y, al lado, cómo se va a ver en el feed (Buffer, Later).
          Si al editor no le alcanza el ancho (el chat abierto, el teléfono), la vista previa baja. */}
      <div
        ref={cuerpoRef}
        className="mt-4 grid items-start gap-4"
        style={lado ? { gridTemplateColumns: `minmax(0,1fr) ${ancho >= 1000 ? 360 : 300}px` } : undefined}
      >
        <div className="min-w-0 space-y-3">
          {/* ── Las piezas ─────────────────────────────────────────────────── */}
          <div data-tour="ig-piezas">
            <Seccion
              titulo="Fotos y videos"
              icono={<Images className="w-4 h-4" />}
              abiertaPorDefecto
              resumen={`${medios.length} de ${MAX_PIEZAS}`}
              aviso={avisoDe('medios')}
            >
              {medios.length > 0 && (
                <p className="text-xs text-neutral-500">
                  Arrastrá para ordenar. La primera es la portada: es la que frena el scroll.
                </p>
              )}

              {/* La MISMA tira que la ficha de un producto
                  (ADR-2026-09-15-las-fotos-de-un-post-se-arrastran-como-las-de-un-producto).
                  El número que dibuja la tira ES el orden del carrusel. */}
              {/* En el teléfono, una tira que se desliza (el "New post" de Instagram): con las
                  miniaturas en filas, dos fotos ya ocupaban la pantalla. */}
              <div className="flex items-start gap-3 max-md:-mx-4 max-md:overflow-x-auto max-md:px-4 max-md:[&_.flex-wrap]:flex-nowrap md:flex-wrap">
                {medios.length > 0 && (
                  <SortableImageList
                    grande
                    imagenes={medios.map((m, i) => ({
                      id: m.id,
                      orden: m.orden,
                      thumbUrl: miniaturas[i] ?? '',
                    }))}
                    onReorder={(ids) => setOrden(ids)}
                    onRemove={(id) => setQuitadas((q) => new Set([...q, id]))}
                    onVer={setViendo}
                    accionExtra={(id) =>
                      medios.find((m) => m.id === id)?.esVideo ? (
                        <span
                          aria-label="Video"
                          title="Video"
                          className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/55 text-white"
                        >
                          <Play className="w-3 h-3" />
                        </span>
                      ) : null
                    }
                  />
                )}
                <AgregarFoto
                  subiendo={subiendo}
                  deshabilitada={medios.length >= MAX_PIEZAS || enInstagram}
                  aceptaVideo={aceptaVideo}
                  tituloDeshabilitada={`Un carrusel admite hasta ${MAX_PIEZAS} piezas`}
                  onElegirGenerada={() => setEligiendo(true)}
                  onSubir={(f) => void subir(f)}
                />
              </div>
            </Seccion>
          </div>

          {/* ── Planificación y referencias: UNA card, el brief del post. Notas de trabajo, no se
              publican; un solo "Crear con IA" que se lleva las dos al agente
              (ADR-2026-10-08-el-post-se-crea-desde-su-planificacion). Oculta por
              `MOSTRAR_PLANIFICACION`. ── */}
          {MOSTRAR_PLANIFICACION && (
          <div data-tour="ig-notas">
            <Seccion
              titulo="Planificación y referencias"
              icono={<Target className="w-4 h-4" />}
              plegable={false}
            >
              <div className="space-y-1.5">
                <label
                  htmlFor="ig-planificacion"
                  className="block text-xs font-semibold uppercase tracking-wide text-neutral-500"
                >
                  Planificación
                </label>
                <textarea
                  id="ig-planificacion"
                  value={planificacion}
                  onChange={(e) => setPlanificacion(e.target.value)}
                  rows={4}
                  placeholder="Para qué es este post, a quién le habla y qué tiene que pasar: el objetivo, la idea, el tono…"
                  className="w-full resize-y rounded-xl border border-neutral-200 py-2.5 px-3 text-sm leading-relaxed text-brand-black outline-none placeholder:text-neutral-400 focus:border-brand-primary focus:ring-4 focus:ring-[rgb(var(--brand-primary-rgb)_/_0.07)] max-md:text-[15px]"
                />
              </div>

              <div className="space-y-1.5 pt-3">
                <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  <Link2 className="w-3.5 h-3.5" /> Referencias
                </span>
                <CajaDeReferencias valor={referencias} onCambiar={setReferencias} />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-neutral-100 pt-3">
                <p className="flex items-center gap-1.5 text-xs text-neutral-400">
                  <Lock className="w-3 h-3" /> Sólo para vos y tu equipo: no se publica.
                </p>
                {botonCrear}
              </div>
            </Seccion>
          </div>
          )}

          {/* ── El copy ────────────────────────────────────────────────────── */}
          <div data-tour="ig-texto">
            <Seccion
              titulo="Descripción"
              icono={<AlignLeft className="w-4 h-4" />}
              plegable={false}
              aviso={avisoDe('texto')}
            >
              {propuestaTexto !== null && propuestaTexto !== texto && (
                <div className="flex flex-wrap items-start gap-3 rounded-xl border border-[rgb(var(--brand-primary-rgb)_/_0.225)] bg-[rgb(var(--brand-primary-rgb)_/_0.07)] p-3">
                  <Sparkles className="mt-0.5 w-4 h-4 shrink-0 text-brand-primary" />
                  <span className="min-w-0 flex-1 text-xs">
                    <span className="block font-semibold text-brand-black">
                      El asistente escribió una descripción nueva
                    </span>
                    <span className="mt-0.5 line-clamp-2 block whitespace-pre-line text-neutral-600">
                      {propuestaTexto || '(vacía)'}
                    </span>
                  </span>
                  <span className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      onClick={() => setPropuestaTexto(null)}
                      className={boton('neutro', { chico: true })}
                    >
                      Ignorar
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setTexto(propuestaTexto);
                        setPropuestaTexto(null);
                      }}
                      className={boton('primario', { chico: true })}
                    >
                      Usarla
                    </button>
                  </span>
                </div>
              )}
              <textarea
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                rows={8}
                placeholder="Qué querés contar con este post… La primera línea es la que se lee antes del «más»."
                className="w-full resize-y rounded-xl border border-neutral-200 py-2.5 px-3 text-sm leading-relaxed max-md:h-36 max-md:text-[15px] text-brand-black outline-none placeholder:text-neutral-400 focus:border-brand-primary focus:ring-4 focus:ring-[rgb(var(--brand-primary-rgb)_/_0.07)]"
              />
              <div className="flex flex-wrap items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => void conIa()}
                  disabled={escribiendo || medios.length === 0}
                  title={medios.length === 0 ? 'Primero sumá una foto o un video' : undefined}
                  className={boton('secundario', { chico: true, extra: 'py-2' })}
                >
                  {escribiendo ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Sparkles className="w-3.5 h-3.5" />
                  )}
                  {escribiendo ? 'Escribiendo…' : 'Escribir con IA'}
                </button>
                <span className="flex shrink-0 items-center gap-2 text-xs text-neutral-500">
                  <span className="rounded-full bg-neutral-100 py-0.5 px-2">
                    {tags} hashtag{tags === 1 ? '' : 's'}
                  </span>
                  <span
                    className={`rounded-full py-0.5 px-2 ${
                      texto.length > MAX_TEXTO ? 'bg-red-50 text-red-700' : 'bg-neutral-100'
                    }`}
                  >
                    {texto.length}/{MAX_TEXTO}
                  </span>
                </span>
              </div>
            </Seccion>
          </div>

          {/* ── Cuándo sale ─────────────────────────────────────────────────── */}
          <div data-tour="ig-cuando">
            <Seccion
              titulo="Cuándo sale"
              icono={<CalendarClock className="w-4 h-4" />}
              plegable={false}
              aviso={avisoDe('cuando')}
            >
              <div className="hidden space-y-3 md:block">{camposDelCuando}</div>
              {/* Teléfono: una fila que abre la hoja con la fecha (LinkedIn, Canva). */}
              <button
                type="button"
                onClick={() => setHoja('cuando')}
                className="flex w-full items-center gap-3 rounded-xl border border-neutral-200 py-3 px-3.5 text-left md:hidden"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-brand-black first-letter:uppercase">
                    {fecha ? fechaLarga(fecha) : 'Elegí el día y la hora'}
                  </span>
                  {!esLaSugerida && !publicado && (
                    <span className="mt-0.5 block text-xs text-brand-primary">
                      Sugerido: {comoSeLee(sugerencia.cuando)}
                    </span>
                  )}
                </span>
                <ChevronRight className="w-4 h-4 shrink-0 text-neutral-400" />
              </button>
            </Seccion>
          </div>
        </div>

        <div
          data-tour="ig-preview"
          className={`max-md:hidden ${lado ? 'sticky top-4' : 'mx-auto w-full max-w-sm'}`}
        >
          <div className="mb-3 rounded-2xl border border-neutral-200 bg-white p-3 shadow-sm">
            <button
              type="button"
              onClick={() => setVerComentarios(true)}
              className={boton('neutro', { extra: 'w-full' })}
            >
              <MessageSquare className="h-4 w-4 text-neutral-500" />
              Comentarios
              {com.comentarios.filter((c) => !c.resuelto).length > 0 && (
                <span className="grid h-5 min-w-5 place-items-center rounded-full bg-brand-primary px-1.5 text-[11px] font-semibold text-white">
                  {com.comentarios.filter((c) => !c.resuelto).length}
                </span>
              )}
            </button>
          </div>
          {vistaPrevia}
        </div>

        {/* Teléfono: la vista previa detrás de una fila; abajo de todo nadie la veía. */}
        <button
          type="button"
          onClick={() => setHoja('vista')}
          data-tour="ig-preview-movil"
          className="flex w-full items-center gap-3 rounded-2xl border border-neutral-200 bg-white p-3.5 text-left shadow-sm md:hidden"
        >
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-neutral-100 text-neutral-500">
            <Eye className="w-4 h-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium text-brand-black">Ver cómo queda en el feed</span>
            <span className="block text-xs text-neutral-500">La portada, el carrusel y el texto</span>
          </span>
          <ChevronRight className="w-4 h-4 shrink-0 text-neutral-400" />
        </button>
      </div>

      {/* Teléfono: "Guardar post" fijo abajo, a lo ancho. */}
      <div
        className="fixed inset-x-0 bottom-0 z-40 border-t border-neutral-100 bg-white/95 px-4 pt-3 backdrop-blur md:hidden"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
      >
        {sinGuardar && !esNuevo && (
          <p className="mb-2 flex items-center justify-center gap-1.5 text-xs text-neutral-500">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> Cambios sin guardar
          </p>
        )}
        <button
          type="button"
          onClick={() => void guardarYVolver()}
          disabled={guardando || enInstagram}
          data-tour="ig-guardar-movil"
          className={boton('primario', { extra: 'w-full py-3.5 text-base' })}
        >
          {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {guardando ? 'Guardando…' : 'Guardar post'}
        </button>
      </div>

      <HojaMovil abierta={hoja === 'acciones'} titulo="Acciones del post" onCerrar={() => setHoja(null)}>
        <div className="divide-y divide-neutral-100 overflow-hidden rounded-2xl border border-neutral-100">
          <button
            type="button"
            onClick={() => {
              setHoja(null);
              void descargar();
            }}
            disabled={medios.length === 0 || bajando}
            className="flex w-full items-center gap-3 py-3.5 px-4 text-left text-sm text-brand-black disabled:opacity-40"
          >
            <Download className="w-4 h-4 text-neutral-500" /> Descargar las piezas
          </button>
          <button
            type="button"
            onClick={() => {
              setHoja(null);
              void copiar();
            }}
            disabled={!texto.trim()}
            className="flex w-full items-center gap-3 py-3.5 px-4 text-left text-sm text-brand-black disabled:opacity-40"
          >
            <ClipboardCopy className="w-4 h-4 text-neutral-500" /> Copiar la descripción
          </button>
          <button
            type="button"
            onClick={() => {
              setHoja(null);
              setVerComentarios(true);
            }}
            className="flex w-full items-center gap-3 py-3.5 px-4 text-left text-sm text-brand-black"
          >
            <MessageSquare className="w-4 h-4 text-neutral-500" /> Comentarios
            {com.comentarios.filter((c) => !c.resuelto).length > 0 && (
              <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-400 px-1 text-[11px] font-semibold text-white">
                {com.comentarios.filter((c) => !c.resuelto).length}
              </span>
            )}
          </button>
        </div>
        <button
          type="button"
          onClick={() => {
            setHoja(null);
            if (esNuevo) void descartar().then(() => terminar({ tipo: 'propia' }));
            else borrar();
          }}
          className="mt-3 flex w-full items-center gap-3 rounded-2xl bg-red-50 py-3.5 px-4 text-left text-sm font-medium text-red-600"
        >
          <Trash2 className="w-4 h-4" /> {esNuevo ? 'Descartar este post' : 'Borrar este post'}
        </button>
      </HojaMovil>

      <HojaMovil abierta={hoja === 'cuando'} titulo="Cuándo sale" onCerrar={() => setHoja(null)}>
        <div className="space-y-3">
          {camposDelCuando}
          <button
            type="button"
            onClick={() => setHoja(null)}
            className={boton('primario', { extra: 'w-full py-3 text-base' })}
          >
            Listo
          </button>
        </div>
      </HojaMovil>

      <HojaMovil abierta={hoja === 'vista'} titulo="Así se ve en el feed" onCerrar={() => setHoja(null)}>
        <div className="mx-auto max-w-sm">{vistaPrevia}</div>
      </HojaMovil>

      <PanelDeComentarios
        abierto={verComentarios}
        nombreDelProducto={texto.split('\n')[0]?.slice(0, 60) || 'este post'}
        comentarios={com.comentarios}
        cargando={com.cargando}
        trabajando={com.trabajando}
        error={com.error}
        onCerrar={() => setVerComentarios(false)}
        onComentar={(t) => void com.dejar(t)}
        onResolver={(id, r, v) => void com.resolver(id, r, v)}
        onBorrar={(id, v) => void com.borrar(id, v)}
      />

      {salida && (
        <PreguntaDeSalida
          esNuevo={esNuevo}
          guardando={guardando}
          onGuardar={() => void guardarYVolver(salida)}
          onDescartar={() => void descartar().then(() => terminar(salida))}
          onQuedarse={() => {
            if (salida.tipo === 'atras') guardia.quedarse(salida);
            if (salida.tipo === 'accion') salida.cancelar?.();
            setSalida(null);
          }}
        />
      )}

      {viendo && (
        <VisorDeFoto
          url={urlAmpliada}
          esVideo={!!post.medios.find((m) => m.id === viendo)?.esVideo}
          onCerrar={() => setViendo(null)}
        />
      )}

      <SelectorDeCreaciones
        abierta={eligiendo}
        companyId={companyId}
        // Por la CREACIÓN y no por el vínculo: `m.id` es la fila de
        // `lanzamientos_imagenes`, y el selector tacha comparando contra
        // `imagenes_generadas.id`. Las subidas a mano no tienen ninguna.
        yaUsadas={medios.map((m) => m.generadaId).filter((x): x is string => !!x)}
        conVideo={aceptaVideo}
        cupo={Math.max(0, MAX_PIEZAS - medios.length)}
        onCerrar={() => setEligiendo(false)}
        onConfirmar={async (elegidas) => {
          if (enInstagram) return setEligiendo(false);
          const r = await sumarGeneradasAProducto(
            post.id,
            companyId,
            elegidas.map((e) => ({ id: e.id, varianteId: e.varianteId })),
          );
          if (r.error) setAviso(r.error);
          setEligiendo(false);
          onCambio();
        }}
      />
    </div>
  );
}

/** Irse con cambios sin guardar: guardar, descartar o quedarse. */
function PreguntaDeSalida({
  esNuevo,
  guardando,
  onGuardar,
  onDescartar,
  onQuedarse,
}: {
  esNuevo: boolean;
  guardando: boolean;
  onGuardar: () => void;
  onDescartar: () => void;
  onQuedarse: () => void;
}) {
  useEffect(() => {
    const alTeclado = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onQuedarse();
    };
    window.addEventListener('keydown', alTeclado);
    return () => window.removeEventListener('keydown', alTeclado);
  }, [onQuedarse]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 sm:items-center"
      onClick={onQuedarse}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="titulo-salida-post"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-t-3xl bg-white p-6 shadow-xl sm:rounded-3xl"
      >
        {/* La cruz es "seguir editando": cerrar la pregunta no decide nada. */}
        <button
          type="button"
          onClick={onQuedarse}
          aria-label="Seguir editando"
          title="Seguir editando"
          className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-brand-black"
        >
          <X className="w-4 h-4" />
        </button>
        <h2 id="titulo-salida-post" className="pr-8 text-lg font-semibold text-brand-black">
          {esNuevo ? '¿Guardás este post?' : '¿Guardás los cambios?'}
        </h2>
        <p className="mt-1.5 text-sm text-neutral-500">
          {esNuevo
            ? 'Todavía no está en tu calendario. Si lo descartás, se pierde lo que armaste.'
            : 'Si los descartás, el post queda como estaba antes de abrirlo.'}
        </p>
        <div className="mt-6 grid grid-cols-2 gap-2">
          <button type="button" onClick={onDescartar} disabled={guardando} className={boton('peligro')}>
            Descartar
          </button>
          <button type="button" onClick={onGuardar} disabled={guardando} className={boton('primario')}>
            {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {esNuevo ? 'Guardar post' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}
